import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  setDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../../sdk_initialization.js';

// Collection name
const LAYOUTS_COLLECTION = 'layouts';
const COUNTERS_COLLECTION = 'counters';
const LAYOUT_ID_COUNTER_DOC = 'layoutId';

/**
 * Generate next layout ID using counter
 * @returns {Promise<string>} - 6-digit layout ID (e.g., '200001')
 */
const generateNextLayoutId = async () => {
  const counterRef = doc(db, COUNTERS_COLLECTION, LAYOUT_ID_COUNTER_DOC);
  
  return await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    
    let nextValue = 200001; // Start at 200001 for 6-digit layout IDs
    
    if (counterDoc.exists()) {
      const currentValue = counterDoc.data().value || 200000;
      nextValue = currentValue + 1;
    }
    
    transaction.set(counterRef, { value: nextValue, updatedAt: serverTimestamp() }, { merge: true });
    
    return String(nextValue).padStart(6, '0');
  });
};

// Helper: recursively remove `undefined` values from objects/arrays so Firestore
// update/set operations do not fail with "Unsupported field value: undefined".
const sanitizeForFirestore = (value) => {
  if (typeof value === 'undefined') return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .map(v => sanitizeForFirestore(v))
      .filter(v => typeof v !== 'undefined');
  }
  // Only treat plain objects (constructed by {}) as containers to sanitize.
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const sv = sanitizeForFirestore(v);
      if (typeof sv !== 'undefined') out[k] = sv;
    });
    return out;
  }
  // For Firestore sentinel values (serverTimestamp, increment, etc.) and other
  // non-plain objects, return as-is.
  return value;
};

/**
 * Create a new layout in Firestore
 * @param {Object} layoutData - Layout data object
 * @param {string} userId - User ID who owns the layout
 * @returns {Promise<string>} - Document ID of the created layout
 */
export const createLayout = async (layoutData, userId) => {
  try {
    if (!db) {
      throw new Error('Database not initialized');
    }
    
    // Generate unique 6-digit layout ID
    const layoutId = await generateNextLayoutId();
    
    const payload = sanitizeForFirestore({
      ...layoutData,
      userId,
      createdByUid: userId,
      updatedByUid: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Use setDoc with the generated layout ID instead of addDoc
    const docRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    await setDoc(docRef, payload);
    
    return docRef;
  } catch (error) {
    console.error('🚨 Error creating layout:', error);
    console.error('🚨 Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    throw error;
  }
};

/**
 * Update an existing layout in Firestore
 * @param {string} layoutId - Document ID of the layout
 * @param {Object} updatedData - Updated layout data
 * @param {string} userId - User ID (for security check)
 * @returns {Promise<void>}
 */
export const updateLayout = async (layoutId, updatedData, userId) => {
  try {
    const layoutRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    // Rely on Firestore security rules for authorization; attach updater for audit
    const clean = sanitizeForFirestore({
      ...updatedData,
      updatedAt: serverTimestamp(),
      updatedByUid: userId
    });
    await updateDoc(layoutRef, clean);
  } catch (error) {
      console.error('Error updating layout:', error);
    throw error;
  }
};

/**
 * Delete a layout from Firestore
 * @param {string} layoutId - Document ID of the layout
 * @param {string} userId - User ID (for security check)
 * @returns {Promise<void>}
 */
export const deleteLayout = async (layoutId, userId) => {
  try {
    const layoutRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    // Rely on Firestore security rules for authorization
    await deleteDoc(layoutRef);
  } catch (error) {
    console.error('Error deleting layout:', error);
    throw error;
  }
};

/**
 * Get all layouts for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of layout objects with IDs
 */
export const getUserLayouts = async (userId, retryCount = 0) => {
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second base delay
  
  try {
    // Try the optimized query with orderBy first
    let q = query(
      collection(db, LAYOUTS_COLLECTION), 
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    let querySnapshot;
    try {
      querySnapshot = await getDocs(q);
    } catch (indexError) {
      // If index not ready, fall back to simple query and sort in memory
      q = query(
        collection(db, LAYOUTS_COLLECTION), 
        where('userId', '==', userId)
      );
      querySnapshot = await getDocs(q);
    }
    
    const layouts = [];
    
    querySnapshot.forEach((doc) => {
      layouts.push({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore Timestamp to ISO string for consistency
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      });
    });
    
    // Sort in memory if we used the fallback query
    if (querySnapshot.size > 0 && !layouts[0].updatedAt) {
      layouts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    }

    // No console logging in production
    return layouts;
  } catch (error) {
    console.error(`🚨 Error fetching user layouts (attempt ${retryCount + 1}):`, error);
    
    // Check if it's a network/unavailable error and we haven't exceeded max retries
    if ((error.code === 'unavailable' || error.code === 'deadline-exceeded' || error.message?.includes('network')) && retryCount < maxRetries) {
      const delay = retryDelay * Math.pow(2, retryCount); // Exponential backoff
      
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return getUserLayouts(userId, retryCount + 1);
    }
    
    throw error;
  }
};

/**
 * Get a single layout by ID (with user verification)
 * @param {string} layoutId - Document ID of the layout
 * @param {string} userId - User ID (for security check)
 * @returns {Promise<Object>} - Layout object with ID
 */
export const getLayout = async (layoutId, userId) => {
  try {
    const layoutRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    const layoutDoc = await getDoc(layoutRef);
    
    if (!layoutDoc.exists()) {
      throw new Error('Layout not found');
    }
    const layoutData = layoutDoc.data();
    return {
      id: layoutDoc.id,
      ...layoutData,
      createdAt: layoutData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      updatedAt: layoutData.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching layout:', error);
    throw error;
  }
};

/**
 * Get a single layout by ID without owner verification (public read for booth selection)
 * WARNING: Use only for read-only public scenarios like event-linked booth selection.
 * @param {string} layoutId - Document ID of the layout
 * @returns {Promise<Object>} - Layout object with ID or throws if missing
 */
export const getLayoutPublic = async (layoutId) => {
  try {
    const layoutRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    const layoutDoc = await getDoc(layoutRef);
    if (!layoutDoc.exists()) {
      throw new Error('Layout not found');
    }
    const layoutData = layoutDoc.data();
    return {
      id: layoutDoc.id,
      ...layoutData,
      createdAt: layoutData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      updatedAt: layoutData.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
    };
  } catch (error) {
    // Only log non-"not found" errors to avoid cluttering console with expected missing layouts
    if (!error.message?.includes('not found')) {
      console.error('Error fetching public layout:', error);
    }
    throw error;
  }
};

/**
 * Update layout status (Draft, Published, Inactive)
 * @param {string} layoutId - Document ID of the layout
 * @param {string} newStatus - New status value
 * @param {string} userId - User ID (for security check)
 * @returns {Promise<void>}
 */
export const updateLayoutStatus = async (layoutId, newStatus, userId) => {
  try {
    await updateLayout(layoutId, { status: newStatus }, userId);
    
  } catch (error) {
    console.error('Error updating layout status:', error);
    throw error;
  }
};

/**
 * Update groupid for specific cells inside a layout document without replacing entire cellData array.
 * @param {string} layoutId
 * @param {Array<{id:string, groupid:string|null}>} updates
 */
export const updateLayoutCellGroupIds = async (layoutId, updates = []) => {
  try {
    if (!Array.isArray(updates) || updates.length === 0) return;
    const layoutRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    // Firestore does not support updating array elements by index safely via field paths.
    // Use a transaction to read-modify-write the entire array atomically.
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(layoutRef);
      if (!snap.exists()) throw new Error('Layout not found');
      let existing = snap.data().cellData || [];
      if (!Array.isArray(existing) && existing && typeof existing === 'object') {
        // legacy shape: object map of id->cell
        existing = Object.values(existing);
      }
      if (!Array.isArray(existing)) existing = [];
      const normalizedUpdates = updates.map(u => ({ id: String(u.id), groupid: u.groupid === undefined ? null : u.groupid }));
      const newCellData = existing.map(cell => {
        const cid = String(cell.id || '');
        const cnum = String(cell.number || '');
        const match = normalizedUpdates.find(u => u.id === cid || u.id === cnum);
        if (match) {
          return { ...cell, groupid: match.groupid };
        }
        return cell;
      });
      tx.update(layoutRef, { cellData: newCellData, updatedAt: serverTimestamp() });
    });
  } catch (error) {
    console.error('Error updating cell group ids:', error);
    throw error;
  }
};

/**
 * Update arbitrary fields for specific cells inside a layout document without replacing entire cellData array.
 * @param {string} layoutId
 * @param {Array<Object>} updates - array of objects where each has an `id` or `number` and any other fields to merge
 */
export const updateLayoutCellFields = async (layoutId, updates = []) => {
  try {
    if (!Array.isArray(updates) || updates.length === 0) return;
    const layoutRef = doc(db, LAYOUTS_COLLECTION, layoutId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(layoutRef);
      if (!snap.exists()) throw new Error('Layout not found');
      let existing = snap.data().cellData || [];
      if (!Array.isArray(existing) && existing && typeof existing === 'object') {
        existing = Object.values(existing);
      }
      if (!Array.isArray(existing)) existing = [];

      const normalizedUpdates = updates.map(u => ({ ...u, id: u.id !== undefined ? String(u.id) : undefined, number: u.number !== undefined ? String(u.number) : undefined }));

      const newCellData = existing.map(cell => {
        const cid = String(cell.id || '');
        const cnum = String(cell.number || '');
        const match = normalizedUpdates.find(u => (u.id && u.id === cid) || (u.number && u.number === cnum));
        if (match) {
          // merge provided fields (except id/number), but strip undefined values
          const { id, number, ...rest } = match;
          const cleanRest = Object.entries(rest).reduce((acc, [k, v]) => {
            if (typeof v !== 'undefined') acc[k] = v;
            return acc;
          }, {});
          return { ...cell, ...cleanRest };
        }
        return cell;
      });

      tx.update(layoutRef, { cellData: newCellData, updatedAt: serverTimestamp() });
    });
  } catch (error) {
    console.error('Error updating cell fields:', error);
    throw error;
  }
};

/**
 * List all layouts (admin/organizer use only)
 * @returns {Promise<Array>} - Array of layout objects with IDs
 */
export const listAllLayouts = async () => {
  try {
    const colRef = collection(db, LAYOUTS_COLLECTION);
    const snap = await getDocs(colRef);
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Sort by updatedAt desc if available
    all.sort((a, b) => {
      const aTime = a.updatedAt?.toDate?.()?.getTime?.() || new Date(a.updatedAt || 0).getTime() || 0;
      const bTime = b.updatedAt?.toDate?.()?.getTime?.() || new Date(b.updatedAt || 0).getTime() || 0;
      return bTime - aTime;
    });
    return all;
  } catch (error) {
    console.error('Error listing all layouts:', error);
    throw error;
  }
};

/**
 * List layouts where a given user is included in the `eventAdmins` array.
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export const listLayoutsByEventAdmin = async (userId) => {
  try {
    const colRef = collection(db, LAYOUTS_COLLECTION);
    const q = query(colRef, where('eventAdmins', 'array-contains', userId));
    const snap = await getDocs(q);
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    all.sort((a, b) => {
      const aTime = a.updatedAt?.toDate?.()?.getTime?.() || new Date(a.updatedAt || 0).getTime() || 0;
      const bTime = b.updatedAt?.toDate?.()?.getTime?.() || new Date(b.updatedAt || 0).getTime() || 0;
      return bTime - aTime;
    });
    return all;
  } catch (error) {
    console.error('Error listing layouts for EventAdmin:', error);
    throw error;
  }
};

